import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
import MainContentStyled from './MainContentStyled';
import Loader from 'ui-component/Loader';
import IntroScreen from './IntroScreen';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state: { borderRadius, miniDrawer, menuOrientation }
  } = useConfig();

  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem('showPhnxIntro') === 'true');

  useEffect(() => {
    handlerDrawerOpen(!miniDrawer);
  }, [miniDrawer]);

  useEffect(() => {
    if (downMD) {
      handlerDrawerOpen(false);
    }
  }, [downMD]);

  useEffect(() => {
    if (!showIntro) return;

    const timer = setTimeout(() => {
      setShowIntro(false);
      sessionStorage.removeItem('showPhnxIntro');

      // one-time flag for KnowledgeGraph immersive auto-open
      sessionStorage.setItem('openKbImmersiveOnce', 'true');
    }, 2000);

    return () => clearTimeout(timer);
  }, [showIntro]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const menu = useMemo(() => (isHorizontal ? <HorizontalBar /> : <Sidebar />), [isHorizontal]);

  if (showIntro) {
    return <IntroScreen />;
  }

  if (menuMasterLoading) {
    return <Loader />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar enableColorOnDark position="fixed" color="inherit" elevation={0} sx={{ bgcolor: 'background.default' }}>
        <Toolbar sx={{ p: isHorizontal ? 1.25 : 2 }}>
          <Header />
        </Toolbar>
      </AppBar>

      {menu}

      <MainContentStyled {...{ borderRadius, menuOrientation, open: drawerOpen }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2,
            minHeight: 'calc(100vh - 88px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Outlet />
          </Box>
        </Box>
      </MainContentStyled>
    </Box>
  );
}